import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  adminApprovalCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { adminEndpoints } from "@/features/admin/api/endpoints";
import type {
  ApprovalRequestDto,
  MarkApprovalRequestFailedDto,
} from "@/features/admin/api/dto";

/**
 * Signale l'échec d'exécution d'une demande APPROVED
 * (`POST /ApprovalRequests/{id}/mark-failed`, motif inclus). Rôles :
 * flashpay-superadmin, flashpay-service. Appelé par le front quand l'action
 * protégée a échoué après approbation.
 */
export const useMarkApprovalFailed = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["admin", "approval-requests", "mark-failed"],
    mutationFn: ({
      approvalRequestId,
      failureReason,
    }: {
      approvalRequestId: string;
      failureReason: string;
    }) =>
      mutateData({
        url: adminEndpoints.approvalRequests.markFailed(approvalRequestId),
        reqBody: { failureReason } satisfies MarkApprovalRequestFailedDto,
        method: "POST",
      }) as Promise<ApprovalRequestDto>,
    onSuccess: (_data, { approvalRequestId }) => {
      return invalidateAfterMutation(
        queryClient,
        adminApprovalCacheKeys(approvalRequestId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useMarkApprovalFailed failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "admin" }
      );
    },
  });
};
